import React, { useState, useContext, useEffect } from 'react';
import { customFetch } from '../util/functions';
import { User } from 'shared/build/types';

type AuthenticationContextT = {
  initializing: boolean;
  user: User | undefined;
  getOtpTokenByPhone: (phoneNumber: string) => Promise<string>;
  loginViaOtp: (otp: string, otpToken: string) => Promise<boolean>;
};

const AuthenticationContext = React.createContext<
  AuthenticationContextT | undefined
>(undefined);

export const AuthenticationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<User | undefined>();
  const [initializing, setInitializing] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      await amILoggedIn();
      setInitializing(false);
    })();
  }, []);

  const getOtpTokenByPhone = async (phoneNumber: string): Promise<string> => {
    const sanitizedPhoneNumber = '+' + phoneNumber.replace(/[^0-9]/g, '');
    const response = await customFetch('/api/auth/get-otp-token', {
      method: 'POST',
      body: JSON.stringify({
        phoneNumber: sanitizedPhoneNumber,
      }),
    });

    if (!response.ok) {
      throw new Error('Falha ao gerar senha!');
    }

    const jsonResponse = await response.json();
    return jsonResponse.otpToken;
  };

  const loginViaOtp = async (
    otp: string,
    otpToken: string
  ): Promise<boolean> => {
    const response = await customFetch('/api/auth/login-via-otp', {
      method: 'POST',
      body: JSON.stringify({
        otp,
        otpToken,
      }),
    });

    if (!response.ok) {
      return false;
    }

    const user = await response.json();
    setUser(user);

    return true;
  };

  const amILoggedIn = async () => {
    const response = await customFetch('/api/auth/am-i-logged-in', {
      method: 'GET',
    });

    if (!response.ok) {
      return false;
    }

    const user = await response.json();
    setUser(user);

    return true;
  };

  return (
    <AuthenticationContext.Provider
      value={{
        user,
        initializing,
        getOtpTokenByPhone,
        loginViaOtp,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuthentication = (): AuthenticationContextT => {
  const value = useContext(AuthenticationContext);

  if (value === undefined)
    throw new Error('The Authentication context had not been initialized.');

  return value;
};
